import RequestProgressService from 'requestProgress/RequestProgressService';
import { SymbolsApi } from './api/SymbolsApi';
import { SymbolValidationResponse } from './api/SymbolValidationResponse';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { LoggingService } from '@instinet-analytics/core-lib';
import { buildLogger, Logger } from '@instinet-analytics/core-lib';
import { Store } from 'redux';
import {
    addSymbolInfo,
    selectSymbolInfo,
    SymbolInfo,
} from './symbolInfo/symbolInfoSlice';
import uniq from 'lodash/uniq';
import { FetchedResultCache } from 'utils/FetchedResultCache';
import { setSymbolStaticData } from 'symbols/store/symbolStaticDataSlice';
import {
    IndexMapping,
    IndexMappingsUserPreferenceKey,
} from 'symbols/indexMappings/store/IndexMappingsState';
import { actions } from 'symbols/indexMappings/store/IndexMappingsSlice';
import { UserPreferencesService } from 'userPreferences/UserPreferencesService';
import { selectUsersSymbology } from 'app/store/appSettings/selectors';

export interface SymbolsServiceOptions {
    api: SymbolsApi;
    requestProgressService: RequestProgressService;
    loggingService: LoggingService;
    userPreferencesService: UserPreferencesService;
}

type ValidateSymbolOptions = { symbol: string; symbology: SymbologyValues };
const symbolValidationResultUid = (options: ValidateSymbolOptions) =>
    `${options.symbol}-${options.symbology}`;

type ConvertSymbolOptions = {
    symbol: string;
    fromSymbology: SymbologyValues;
    toSymbology: SymbologyValues;
};

const symbolConvertResultUid = (options: ConvertSymbolOptions) =>
    `${options.symbol}-${options.fromSymbology}-${options.toSymbology}`;

export class SymbolsService {
    private api: SymbolsApi;
    private requestProgressService: RequestProgressService;
    private logger: Logger;
    private fetchStaticDataInvoked = false;
    private userPreferencesService: UserPreferencesService;

    private validationResultCache: FetchedResultCache<
        SymbolValidationResponse,
        ValidateSymbolOptions
    >;
    private convertSymbolCache: FetchedResultCache<
        string | undefined,
        ConvertSymbolOptions
    >;
    public store: Store | undefined;

    public constructor(options: SymbolsServiceOptions) {
        this.api = options.api;
        this.requestProgressService = options.requestProgressService;
        this.logger = buildLogger('SymbolsService', options.loggingService);
        this.validationResultCache = new FetchedResultCache<
            SymbolValidationResponse,
            ValidateSymbolOptions
        >({
            logger: this.logger,
            toUid: symbolValidationResultUid,
            fetchResult: this.validateSymbolEx.bind(this),
            onNewResultFetched: this.onNewResultFetched.bind(this),
            timeoutMins: undefined,
        });
        this.convertSymbolCache = new FetchedResultCache<
            string | undefined,
            ConvertSymbolOptions
        >({
            logger: this.logger,
            toUid: symbolConvertResultUid,
            fetchResult: this.convertSymbolEx.bind(this),
            timeoutMins: undefined,
        });
        this.userPreferencesService = options.userPreferencesService;
    }

    public async validateSymbol(
        symbol: string,
        symbology: SymbologyValues
    ): Promise<SymbolValidationResponse> {
        return this.validationResultCache.fetch({ symbol, symbology });
    }

    private async validateSymbolEx(
        options: ValidateSymbolOptions
    ): Promise<SymbolValidationResponse> {
        const { symbol, symbology } = options;

        this.logger.debug(`Validating "${symbol}" in symbology "${symbology}"`);

        return await this.requestProgressService.invokeAndMonitorRequest(
            'validateSymbol',
            (options) => this.api.validateSymbol(symbol, symbology, options)
        );
    }

    private onNewResultFetched(
        _options: ValidateSymbolOptions,
        response: SymbolValidationResponse
    ) {
        if (response?.symbolInfo?.inst) {
            this.store!.dispatch(
                addSymbolInfo({
                    iSymbol: response.symbolInfo.inst,
                    bloomSymbol: response.symbolInfo.bbg,
                    assetClass: response.symbolInfo.ac,
                    country: response.symbolInfo.ctry,
                    exchangeSymbol: response.symbolInfo.exch,
                    currencyCode: response.symbolInfo.ccy,
                    description: response.symbolInfo.desc,
                    isin: response.symbolInfo.isin,
                    region: response.symbolInfo.reg,
                    symbolIndex: response.symbolInfo.symbolIndex,
                    industry: response.symbolInfo.sector,
                    sector: response.symbolInfo.ind,
                    subIndustry: response.symbolInfo.subInd,
                    superSector: response.symbolInfo.superSector,
                    securityType: response.symbolInfo.secType,
                })
            );
        }
    }

    public async convertSymbol(
        symbol: string | undefined,
        fromSymbology: SymbologyValues,
        toSymbology: SymbologyValues
    ): Promise<string | undefined> {
        if (!symbol || symbol === '') {
            return '';
        }
        if (fromSymbology === toSymbology) {
            return symbol;
        }

        return await this.convertSymbolCache.fetch({
            symbol,
            fromSymbology,
            toSymbology,
        });
    }

    public async getSymbolInUsersSymbology(
        symbol: string | undefined,
        fromSymbology: SymbologyValues
    ) {
        const usersSymbology = selectUsersSymbology(this.store!.getState());
        return await this.convertSymbol(symbol, fromSymbology, usersSymbology);
    }

    public async convertIndexMappingsSymbologies(
        from: IndexMapping[] | undefined,
        fromSymbology: SymbologyValues,
        toSymbology: SymbologyValues
    ): Promise<IndexMapping[] | undefined> {
        if (!from) {
            return undefined;
        }
        if (fromSymbology === toSymbology) {
            return from;
        }
        if (from.length === 0) {
            return [];
        }

        const indexes = from.map((m) => m.index).filter((m) => !!m);

        const convertedSymbols = await this.convertSymbolsEx(
            uniq(indexes),
            fromSymbology,
            toSymbology
        );

        return from.map((f) => ({
            ...f,
            index: convertedSymbols[f.index],
        }));
    }

    public async getSymbolInfo(
        symbol: string,
        symbology: SymbologyValues
    ): Promise<SymbolInfo | undefined> {
        const validated = await this.validateSymbol(symbol, symbology);
        return selectSymbolInfo(
            this.store!.getState(),
            validated?.symbolInfo?.inst
        );
    }

    public hasSymbolPassedValidation(
        symbology: SymbologyValues,
        symbol: string
    ) {
        return this.validationResultCache.hasCachedResult({
            symbol,
            symbology,
        });
    }

    public fetchStaticData() {
        if (this.fetchStaticDataInvoked) {
            return;
        }

        this.fetchStaticDataInvoked = true;

        this.requestProgressService
            .invokeAndMonitorRequest('getSymbolStaticData', (options) =>
                this.api.getStaticData(options)
            )
            .then((data) => {
                this.logger.debug('Fetched symbol static data');
                this.store!.dispatch(setSymbolStaticData(data));
            })
            .catch((err) => {
                this.logger.warn('Error fetching symbol static data', err);
            });
    }

    private async convertSymbolEx(
        options: ConvertSymbolOptions
    ): Promise<string | undefined> {
        const { symbol, fromSymbology, toSymbology } = options;

        this.logger.debug(
            `Converting "${symbol}" from symbology "${fromSymbology}" to "${toSymbology}"`
        );

        const response = await this.convertSymbolsEx(
            [symbol],
            fromSymbology,
            toSymbology
        );

        return response[symbol];
    }

    private async convertSymbolsEx(
        symbols: string[],
        fromSymbology: SymbologyValues,
        toSymbology: SymbologyValues
    ) {
        return await this.requestProgressService.invokeAndMonitorRequest(
            'convertSymbols',
            (options) =>
                this.api.convert(symbols, fromSymbology, toSymbology, options)
        );
    }

    public async saveIndexMappings(
        symbology: SymbologyValues,
        mappings: IndexMapping[]
    ) {
        this.store!.dispatch(actions.setMappings({ symbology, mappings }));
        const nextUserPreferences = await this.convertIndexMappingsSymbologies(
            mappings,
            symbology,
            SymbologyValues.Instinet
        );

        this.store!.dispatch(
            actions.setMappings({
                symbology: SymbologyValues.Instinet,
                mappings: nextUserPreferences ?? [],
            })
        );

        if (nextUserPreferences) {
            await this.userPreferencesService.saveUserPreference(
                IndexMappingsUserPreferenceKey,
                nextUserPreferences
            );
        }
    }

    public async processTemplate(
        symbol: string,
        template: string,
        symbology?: SymbologyValues
    ) {
        const usersSymbology = selectUsersSymbology(this.store!.getState());
        const info = await this.getSymbolInfo(
            symbol,
            symbology ?? usersSymbology
        );
        const symbolInUsersSymbology = await this.getSymbolInUsersSymbology(
            symbol,
            symbology ?? usersSymbology
        );

        return this.applyTemplate(
            template,
            symbolInUsersSymbology ?? symbol,
            info?.description
        );
    }

    public applyTemplate(
        template: string,
        symbol: string,
        symbolName?: string
    ) {
        return template
            .replace(/{symbol}/gi, symbol)
            .replace(/{symbolName}/gi, symbolName ?? symbol);
    }
}
